import { Flex, Text, Button } from "@radix-ui/themes";
import { Step } from "@/v2/features/sampling/Step";
import { EscalationRuleFormData } from "../EscalationRuleForm";
import { SelectOption } from "@/components/shared/select";
import { EscalationStrategy } from "@/v2/entities/release/Release.model";
import { useEscalationRuleApi } from "@/v2/entities/escalation-rule/EscalationRule.api";
import { useQuery } from "react-query";
import { useGetAgents } from "@/api/agent-group";
import { useEnrichmentApi } from "@/v2/entities/enrichment/Enrichment.api";
import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { PlusIcon } from "lucide-react";
import { EscalationRuleOperationType } from "@/v2/entities/escalation-rule/EscalationRule.model";
import { PrioritySection } from "./sections/PrioritySection";
import { TagsSection } from "./sections/TagsSection";
import { AssigneeSection } from "./sections/AssigneeSection";
import { CustomFieldsSection } from "./sections/CustomFieldsSection";
import { SubjectSection } from "./sections/SubjectSection";

interface ActionStepProps {
  value: EscalationRuleFormData;
  simulatorId: string | undefined;
  onChange: (updates: Partial<EscalationRuleFormData>) => void;
}

export function ActionStep({ value, simulatorId, onChange }: ActionStepProps) {
  const { getTicketFields } = useEscalationRuleApi(simulatorId);
  const { data: agents, isLoading: loadingAgents } = useGetAgents();
  const { data: ticketFields, isLoading: loadingTicketFields } = useQuery(
    "ticketFields",
    getTicketFields
  );
  const enrichmentApi = useEnrichmentApi();
  const { data: enrichments, isLoading: loadingEnrichments } = useQuery(
    "enrichments",
    () => enrichmentApi.getAll()
  );

  const [activeSections, setActiveSections] = useState<Set<string>>(new Set());
  const [tags, setTags] = useState<string[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [customFields, setCustomFields] = useState<
    Array<{
      fieldId: string;
      fieldName: string;
      value: string;
      valueName: string;
    }>
  >([]);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && value.escalationRuleOperations.length > 0) {
      const initialSections = new Set<string>();
      const initialTags: string[] = [];
      let initialAgent = "";
      let initialSubject = "";

      value.escalationRuleOperations.forEach((operation) => {
        switch (operation.type) {
          case EscalationRuleOperationType.ADD_PRIORITY:
            initialSections.add("priority");
            break;
          case EscalationRuleOperationType.ADD_TAG:
            initialSections.add("tags");
            if (operation.value) {
              initialTags.push(operation.value);
            }
            break;
          case EscalationRuleOperationType.ADD_ASSIGNEE:
            initialSections.add("assignee");
            if (operation.key) {
              initialAgent = operation.key;
            }
            break;
          case EscalationRuleOperationType.ADD_CUSTOM_FIELD:
            break;
          case EscalationRuleOperationType.ADD_SUBJECT:
            initialSections.add("subject");
            if (operation.value) {
              initialSubject = operation.value;
            }
            break;
        }
      });

      setActiveSections(initialSections);
      setTags(initialTags);
      setSelectedAgent(initialAgent);
      setSubject(initialSubject);
      hasInitialized.current = true;
    }
  }, [value.escalationRuleOperations, ticketFields, agents]);

  useEffect(() => {
    if (value.subject && value.subject !== subject) {
      setSubject(value.subject);
    }
  }, [value.subject, subject]);

  useEffect(() => {
    if (ticketFields && value.escalationRuleOperations.length > 0) {
      const customFieldOperations = value.escalationRuleOperations.filter(
        (operation) =>
          operation.type === EscalationRuleOperationType.ADD_CUSTOM_FIELD
      );

      if (customFieldOperations.length > 0) {
        const initialCustomFields: Array<{
          fieldId: string;
          fieldName: string;
          value: string;
          valueName: string;
        }> = [];

        customFieldOperations.forEach((operation) => {
          if (operation.key && operation.value) {
            const selectedField = ticketFields.find(
              (field) => field.id === operation.key
            );
            if (selectedField) {
              const selectedOption = selectedField.customFieldOptions?.find(
                (option) => option.value === operation.value
              );

              initialCustomFields.push({
                fieldId: operation.key,
                fieldName: selectedField.name,
                value: operation.value,
                valueName: selectedOption?.name || operation.value,
              });
            }
          }
        });

        setCustomFields(initialCustomFields);
        setActiveSections((prev) => new Set([...prev, "customfield"]));
      }
    }
  }, [ticketFields, value.escalationRuleOperations]);

  const agentOptions = useMemo<SelectOption[]>(() => {
    return (
      agents
        ?.filter((agent) => !!agent.name)
        ?.map(({ name, externalId }) => ({
          value: externalId,
          label: name,
        })) ?? []
    );
  }, [agents]);

  const isAutomatedMessage =
    value.escalationStrategy === EscalationStrategy.NONE;

  const isCreateTicket =
    value.escalationStrategy === EscalationStrategy.CREATE_TICKET;

  const addSection = useCallback(
    (sectionType: string) => {
      setActiveSections((prev) => new Set([...prev, sectionType]));
      if (sectionType === "priority") {
        const existingPriorityOperation = value.escalationRuleOperations.find(
          (operation) =>
            operation.type === EscalationRuleOperationType.ADD_PRIORITY
        );

        if (!existingPriorityOperation) {
          const newOperations = [
            ...value.escalationRuleOperations,
            {
              type: EscalationRuleOperationType.ADD_PRIORITY,
              enabled: true,
              key: null,
              value: "medium",
            },
          ];

          onChange({
            escalationRuleOperations: newOperations,
          });
        }
      } else if (sectionType === "assignee") {
        const existingAssigneeOperation = value.escalationRuleOperations.find(
          (operation) =>
            operation.type === EscalationRuleOperationType.ADD_ASSIGNEE
        );

        if (!existingAssigneeOperation) {
          const newOperations = [
            ...value.escalationRuleOperations,
            {
              type: EscalationRuleOperationType.ADD_ASSIGNEE,
              enabled: true,
              key: null,
              value: "",
            },
          ];

          onChange({
            escalationRuleOperations: newOperations,
          });
        }
      } else if (sectionType === "customfield") {
        const existingCustomFieldOperation =
          value.escalationRuleOperations.find(
            (operation) =>
              operation.type === EscalationRuleOperationType.ADD_CUSTOM_FIELD
          );

        if (!existingCustomFieldOperation) {
          const newOperations = [...value.escalationRuleOperations];

          onChange({
            escalationRuleOperations: newOperations,
          });
        }
      } else if (sectionType === "subject") {
        const existingSubjectOperation = value.escalationRuleOperations.find(
          (operation) =>
            operation.type === EscalationRuleOperationType.ADD_SUBJECT
        );

        if (!existingSubjectOperation) {
          const newOperations = [
            ...value.escalationRuleOperations,
            {
              type: EscalationRuleOperationType.ADD_SUBJECT,
              enabled: true,
              key: null,
              value: "",
            },
          ];

          onChange({
            escalationRuleOperations: newOperations,
          });
        }
      }
    },
    [value.escalationRuleOperations, onChange]
  );

  const removeSection = useCallback(
    (sectionType: string) => {
      setActiveSections((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sectionType);
        return newSet;
      });

      let operationTypeToRemove: EscalationRuleOperationType | null = null;
      switch (sectionType) {
        case "priority":
          operationTypeToRemove = EscalationRuleOperationType.ADD_PRIORITY;
          break;
        case "tags":
          operationTypeToRemove = EscalationRuleOperationType.ADD_TAG;
          setTags([]);
          break;
        case "assignee":
          operationTypeToRemove = EscalationRuleOperationType.ADD_ASSIGNEE;
          setSelectedAgent("");
          break;
        case "customfield":
          operationTypeToRemove = EscalationRuleOperationType.ADD_CUSTOM_FIELD;
          setCustomFields([]);
          break;
        case "subject":
          operationTypeToRemove = EscalationRuleOperationType.ADD_SUBJECT;
          break;
      }

      if (operationTypeToRemove) {
        onChange({
          escalationRuleOperations: value.escalationRuleOperations.filter(
            (operation) => operation.type !== operationTypeToRemove
          ),
        });
      }
    },
    [value.escalationRuleOperations, onChange]
  );

  const dropdownItems = useMemo<
    { label: string; onClick: () => void }[]
  >(() => {
    const subjectOption = [
      {
        label: "Subject",
        onClick: () => addSection("subject"),
      },
    ];
    const allOptions = [
      {
        label: "Priority",
        onClick: () => addSection("priority"),
      },
      {
        label: "Tags",
        onClick: () => addSection("tags"),
      },
      {
        label: "Assignee",
        onClick: () => addSection("assignee"),
      },
      {
        label: "Custom Fields",
        onClick: () => addSection("customfield"),
      },
      ...subjectOption,
    ];

    return allOptions.filter((option) => {
      const sectionType = option.label.toLowerCase();
      const normalizedSectionType =
        sectionType === "custom fields" ? "customfield" : sectionType;
      return !activeSections.has(normalizedSectionType);
    });
  }, [addSection, activeSections]);

  const showAutomatedMessage = isAutomatedMessage;
  const showCreateTicket = isCreateTicket && value.name !== "Default Rule";

  if (!showAutomatedMessage && !showCreateTicket) {
    return null;
  }

  return (
    <Step
      index={3}
      label="Action"
      description="Choose what happens when the rule triggers"
    >
      <Flex
        direction={"column"}
        gap={"4"}
        style={{ width: "400px", padding: "4px" }}
      >
        {isAutomatedMessage && (
          <Flex direction={"column"} gap={"3"}>
            <Textarea
              placeholder="Enter the automated message that will be sent to the user..."
              value={value.systemMessage || ""}
              onChange={(e) => onChange({ systemMessage: e.target.value })}
              className="min-h-[100px] resize-y"
            />
            <Text size="1" color="gray">
              This message will be sent to the user when the escalation rule
              triggers.
            </Text>
          </Flex>
        )}
        {isCreateTicket && value.name !== "Default Rule" && (
          <div>
            {/* Only show Add Rule button if there are available options */}
            {dropdownItems.length > 0 && (
              <DropdownMenu
                items={dropdownItems.map((option) => ({
                  key: option.label,
                  label: option.label,
                  onClick: () => {
                    option.onClick();
                  },
                  icon: <PlusIcon size={14} />,
                }))}
              >
                <DropdownMenu.Trigger>
                  <Button
                    color="gray"
                    variant="surface"
                    highContrast
                    size="2"
                    style={{ marginBottom: "12px" }}
                  >
                    <PlusIcon size={15} />
                    Add Action
                  </Button>
                </DropdownMenu.Trigger>
              </DropdownMenu>
            )}

            {/* Priority Section */}
            {activeSections.has("priority") && (
              <PrioritySection
                value={value}
                onChange={onChange}
                onRemove={() => removeSection("priority")}
              />
            )}

            {/* Tags Section */}
            {activeSections.has("tags") && (
              <TagsSection
                value={value}
                onChange={onChange}
                onRemove={() => removeSection("tags")}
                tags={tags}
                setTags={setTags}
                enrichments={enrichments || []}
                loadingEnrichments={loadingEnrichments}
              />
            )}

            {/* Assignee Section */}
            {activeSections.has("assignee") && (
              <AssigneeSection
                value={value}
                onChange={onChange}
                onRemove={() => removeSection("assignee")}
                selectedAgent={selectedAgent}
                setSelectedAgent={setSelectedAgent}
                agentOptions={agentOptions}
                loadingAgents={loadingAgents}
              />
            )}

            {/* Custom Field Section */}
            {activeSections.has("customfield") && (
              <CustomFieldsSection
                value={value}
                onChange={onChange}
                onRemove={() => removeSection("customfield")}
                customFields={customFields}
                setCustomFields={setCustomFields}
                ticketFields={ticketFields}
                loadingTicketFields={loadingTicketFields}
                enrichments={enrichments || []}
                loadingEnrichments={loadingEnrichments}
              />
            )}

            {/* Subject Section */}
            {activeSections.has("subject") && (
              <SubjectSection
                value={value}
                setSubject={setSubject}
                onChange={onChange}
                onRemove={() => removeSection("subject")}
              />
            )}
          </div>
        )}
      </Flex>
    </Step>
  );
}
